import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Grid, Tabs, Tab, Card, CardContent, Avatar, Button, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Email, Phone, LocationOn, Person, AttachMoney as AttachMoneyIcon, Business, Description, Assignment } from '@mui/icons-material';
import { getAllBankerDetailsById } from '../../../services/admin.service';
import Loader from '../../../common/loader';
import { formatPhoneNumber } from '../../../helper/phoneFormatter.helper';
import { formatCurrency } from '../../../helper/currencyFormatter.helper';

export const AdminBankerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [banker, setBanker] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('user');

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchBankerDetails = async () => {
      if (!id) return;
      try {
        const data = await getAllBankerDetailsById(id);
        setBanker(data);
      } catch (error) {
        console.error('Error fetching banker details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBankerDetails();
  }, [id]);

  if (loading) return <Loader />;

  if (!banker) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Banker not found</Typography>
      </Box>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        <div className="mb-4 sm:mb-6 md:mb-8">
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: { xs: 2, sm: 3 },
              p: { xs: 2, sm: 3, md: 4 },
              mb: 2,
              color: 'white',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
              <div className="flex-1">
                <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: { xs: 0, sm: 1 }, fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' } }}>
                  Banker Details
                </Typography>
              </div>
            </div>
          </Box>
        </div>

        <Box className="mb-3 bg-white p-2 sm:p-3 md:p-4 lg:p-5 rounded-xl shadow-sm border border-gray-100">
          <Tabs
            value={activeTab}
            onChange={handleChange}
            centered={!isMobile}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile={isMobile}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              "& .MuiTab-root": {
                minHeight: { xs: 60, sm: 70, md: 80, lg: 85 },
                minWidth: { xs: 70, sm: 90, md: 110, lg: 120 },
                borderRadius: { xs: "10px", sm: "12px", md: "16px", lg: "18px" },
                marginX: { xs: 0.25, sm: 0.5, md: 1, lg: 1.5 },
                textTransform: "none",
                fontWeight: 500,
                color: "#6b7280",
                transition: "all 0.3s ease",
                display: "flex",
                flexDirection: "column",
                gap: { xs: "2px", sm: "4px", md: "8px", lg: "10px" },
                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem', lg: '0.9rem' },
                padding: { xs: '6px 3px', sm: '8px 4px', md: '12px 8px', lg: '14px 10px' },
              },
              "& .MuiTab-root:hover": {
                backgroundColor: "#f9fafb",
                transform: "translateY(-1px)",
              },
              "& .Mui-selected": {
                background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)",
                color: "white !important",
                boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
                transform: "translateY(-1px)",
                "& .MuiSvgIcon-root.new-loan-icon": {
                  color: "white !important",
                },
              },
              "& .MuiTabs-scrollButtons": {
                display: { xs: 'flex', sm: 'none' },
              },
              "& .MuiTabs-flexContainer": {
                justifyContent: { xs: 'flex-start', sm: 'center', md: 'center', lg: 'center' },
                gap: { xs: 0.25, sm: 0.5, md: 1, lg: 1.5 },
              },
            }}
          >
            <Tab
              value="user"
              label={
                <div className="flex flex-col items-center">
                  <img
                    src="https://img.icons8.com/color/96/user-male-circle--v1.png"
                    alt="User Info"
                    className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1"
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">User Info</span>
                </div>
              }
            />
            <Tab
              value="registration"
              label={
                <div className="flex flex-col items-center">
                  <Assignment
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1 ${activeTab === "registration" ? "text-white" : "text-blue-500"}`}
                    fontSize="large"
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">Registration</span>
                </div>
              }
            />
            <Tab
              value="deals"
              label={
                <div className="flex flex-col items-center">
                  <AttachMoneyIcon
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1 new-loan-icon ${
                      activeTab === "deals" ? "text-white" : "text-blue-500"
                    }`}
                    fontSize="large"
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">Deals</span>
                </div>
              }
            />
            <Tab
              value="documents"
              label={
                <div className="flex flex-col items-center">
                  <img
                    src="https://img.icons8.com/color/96/documents.png"
                    alt="Documents"
                    className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mb-0.5 sm:mb-1"
                  />
                  <span className="text-xs sm:text-sm font-medium leading-tight">Documents</span>
                </div>
              }
            />
          </Tabs>
        </Box>

        {activeTab === 'user' && (
          <Box sx={{ mx: 'auto', maxWidth: '100%' }}>
            {/* Profile Header Card */}
            <Card sx={{
              mb: 4,
              borderRadius: { xs: 3, sm: 4, lg: 5 },
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: 150, sm: 200, lg: 250 },
                height: { xs: 150, sm: 200, lg: 250 },
                borderRadius: '50%',
                transform: 'translate(50%, -50%)',
              },
            }}>
              <CardContent sx={{ py: { xs: 3, sm: 4, md: 5, lg: 6 }, px: { xs: 3, sm: 4, lg: 5 }, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' }, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: { xs: 60, sm: 70, md: 80, lg: 90 },
                      height: { xs: 60, sm: 70, md: 80, lg: 90 },
                      mr: { xs: 0, sm: 3 },
                      mb: { xs: 2, sm: 0 },
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                    }}
                    src={banker.signature ? `${process.env.REACT_APP_API_URL}/${banker.signature}` : undefined}
                  >
                    <Person sx={{ fontSize: { xs: 30, sm: 35, md: 40, lg: 45 } }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem', lg: '2.5rem' } }}>
                      {`${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`.trim()}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    px: 3,
                    py: 1.5,
                    borderRadius: 25,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)',
                    },
                  }}>
                    <Business sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '0.95rem' }}>
                      Banker
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(34, 197, 94, 0.2)',
                    px: 3,
                    py: 1.5,
                    borderRadius: 25,
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(34, 197, 94, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                  }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#22c55e',
                      mr: 1.5,
                      animation: 'pulse 2s infinite',
                    }} />
                    <Typography variant="body2" sx={{ fontWeight: '600', color: '#22c55e', fontSize: '0.9rem' }}>
                      Active Banker
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Contact Information Card */}
            <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
              <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                <Card sx={{
                  height: '100%',
                  borderRadius: { xs: 2, sm: 3, lg: 4 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  },
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, lg: 4 } }}>
                    <Typography variant="h6" sx={{ mb: { xs: 2, sm: 3, lg: 4 }, fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.1rem', sm: '1.25rem', lg: '1.4rem' } }}>
                      Contact Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                      <Box sx={{
                        bgcolor: 'primary.main',
                        borderRadius: '50%',
                        p: { xs: 0.75, sm: 1 },
                        mr: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Email sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Email Address</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' }, wordBreak: 'break-word' }}>{banker.email}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{
                        bgcolor: 'success.main',
                        borderRadius: '50%',
                        p: { xs: 0.75, sm: 1 },
                        mr: { xs: 1.5, sm: 2 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Phone sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Phone Number</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }}>{formatPhoneNumber(banker.phone)}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Address Information Card */}
              <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                <Card sx={{
                  height: '100%',
                  borderRadius: { xs: 2, sm: 3, lg: 4 },
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  },
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3, lg: 4 } }}>
                    <Typography variant="h6" sx={{ mb: { xs: 2, sm: 3, lg: 4 }, fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.1rem', sm: '1.25rem', lg: '1.4rem' } }}>
                      Address Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                      <LocationOn sx={{ mr: { xs: 1.5, sm: 2 }, color: 'warning.main', mt: 0.5, fontSize: { xs: 18, sm: 20 } }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Street Address</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' }, wordBreak: 'break-word' }}>{banker.street || 'N/A'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: { xs: 'auto', sm: 80 }, mb: { xs: 0.5, sm: 0 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>City:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }}>{banker.city || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: { xs: 'auto', sm: 80 }, mb: { xs: 0.5, sm: 0 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>State:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }}>{banker.state || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: { xs: 'auto', sm: 80 }, mb: { xs: 0.5, sm: 0 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Zipcode:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }}>{banker.zipcode || 'N/A'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeTab === 'registration' && (
          <Box sx={{ mx: 'auto', maxWidth: '100%' }}>
            {banker.bankerRegistration ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 4 } }}>
                {/* Bank Information */}
                <Card sx={{
                  borderRadius: { xs: 3, sm: 4, lg: 5 },
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4, lg: 5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                      <Box sx={{
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1.5, sm: 2 },
                      }}>
                        <Business sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        Bank Information
                      </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(102, 126, 234, 0.04)',
                          border: '1px solid rgba(102, 126, 234, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Associated Bank</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{banker.bankerRegistration.associatedBank || 'N/A'}</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(102, 126, 234, 0.04)',
                          border: '1px solid rgba(102, 126, 234, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Work Email</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' }, wordBreak: 'break-word' }}>{banker.bankerRegistration.workEmail || 'N/A'}</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(102, 126, 234, 0.04)',
                          border: '1px solid rgba(102, 126, 234, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Preferred Deal Types</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' }, wordBreak: 'break-word' }}>{banker.bankerRegistration.preferredDealTypes || 'N/A'}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Financial Criteria */}
                <Card sx={{
                  borderRadius: { xs: 3, sm: 4, lg: 5 },
                  boxShadow: '0 12px 40px rgba(34, 197, 94, 0.15)',
                  border: '1px solid rgba(34, 197, 94, 0.1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                  },
                }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4, lg: 5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                      <Box sx={{
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: { xs: 1.5, sm: 2 },
                      }}>
                        <AttachMoneyIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        Financial Criteria
                      </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(34, 197, 94, 0.04)',
                          border: '1px solid rgba(34, 197, 94, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Max LTV</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{banker.bankerRegistration.maxLTV ? `${banker.bankerRegistration.maxLTV}%` : 'N/A'}</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(34, 197, 94, 0.04)',
                          border: '1px solid rgba(34, 197, 94, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Min DSC Ratio</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{banker.bankerRegistration.minDSCRatio || 'N/A'}</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          bgcolor: 'rgba(34, 197, 94, 0.04)',
                          border: '1px solid rgba(34, 197, 94, 0.08)',
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Required Collateral Coverage</Typography>
                          <Typography variant="body1" sx={{ fontWeight: '600', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{banker.bankerRegistration.requiredCollateralCoverage ? `${banker.bankerRegistration.requiredCollateralCoverage}%` : 'N/A'}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Card sx={{
                borderRadius: { xs: 3, sm: 4 },
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                textAlign: 'center',
                py: { xs: 6, sm: 8, md: 10 },
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '2px dashed rgba(102, 126, 234, 0.2)',
              }}>
                <CardContent>
                  <Box sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 3, sm: 4 },
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                  }}>
                    <Business sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    No Registration Data Available
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    This banker has not completed their registration process yet.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Registration information will appear here once the banker submits their details.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {activeTab === 'deals' && (
          <Box sx={{ mx: 'auto', maxWidth: '100%' }}>
            {banker.assignedDeals && banker.assignedDeals.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {banker.assignedDeals.map((deal: any) => (
                  <Card key={deal.id} sx={{
                    borderRadius: { xs: 3, sm: 4, lg: 5 },
                    boxShadow: '0 12px 40px rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.1)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                    },
                  }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4, lg: 5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                        }}>
                          <AttachMoneyIcon sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {deal.loanDeal?.loanName || 'Deal Details'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Deal ID: {deal.loanDeal?.id}
                          </Typography>
                        </Box>
                      </Box>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(34, 197, 94, 0.04)',
                            border: '1px solid rgba(34, 197, 94, 0.08)',
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Interest Status</Typography>
                            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1.1rem' }}>{deal.interestStatus}</Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(34, 197, 94, 0.04)',
                            border: '1px solid rgba(34, 197, 94, 0.08)',
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Deal Type</Typography>
                            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1.1rem' }}>{deal.loanDeal?.dealType || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(34, 197, 94, 0.04)',
                            border: '1px solid rgba(34, 197, 94, 0.08)',
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Loan Size</Typography>
                            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1.1rem' }}>{formatCurrency(deal.loanDeal?.loanSize)}</Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(34, 197, 94, 0.04)',
                            border: '1px solid rgba(34, 197, 94, 0.08)',
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Status</Typography>
                            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1.1rem' }}>{deal.loanDeal?.status || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Term Sheets */}
                      {banker.termSheets && banker.termSheets.filter((ts: any) => ts.dealId === deal.loanDeal?.id).length > 0 && (
                        <Box sx={{ mt: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                            Term Sheet
                          </Typography>
                          {banker.termSheets.filter((ts: any) => ts.dealId === deal.loanDeal?.id).map((termSheet: any) => (
                            <Card key={termSheet.id} sx={{
                              borderRadius: 3,
                              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.08)',
                              border: '1px solid rgba(59, 130, 246, 0.1)',
                            }}>
                              <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Loan Type</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{termSheet.loanType}</Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Facility Type</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{termSheet.facilityType}</Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Loan Amount</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{formatCurrency(termSheet.loanAmount)}</Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Tenor/Maturity</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{termSheet.tenorMaturity}</Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Pricing</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{termSheet.pricing}</Typography>
                                    </Box>
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: 'rgba(59, 130, 246, 0.04)',
                                      border: '1px solid rgba(59, 130, 246, 0.08)',
                                    }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Fees</Typography>
                                      <Typography variant="body1" sx={{ fontWeight: '600' }}>{termSheet.fees}</Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Card sx={{
                borderRadius: 4,
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                textAlign: 'center',
                py: 10,
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '2px dashed rgba(102, 126, 234, 0.2)',
              }}>
                <CardContent>
                  <Box sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 4,
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                  }}>
                    <AttachMoneyIcon sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                    No Assigned Deals Available
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                    This banker has not been assigned to any deals yet.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                    Deal information will appear here once the banker is assigned to loan deals.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {activeTab === 'documents' && (
          <Box sx={{ mx: 'auto', maxWidth: '100%' }}>
            {banker.uploadedDocuments && banker.uploadedDocuments.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {banker.uploadedDocuments.map((doc: any) => (
                  <Card
                    key={doc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: { xs: 2, sm: 3, lg: 4 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        borderColor: 'warning.main'
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3, lg: 4 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Description sx={{ color: 'warning.main', fontSize: 20 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {doc.docName}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
                        {doc.isCustomerUpload ? doc.customerOriginalName : doc.originalName}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 1 : 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(`${process.env.REACT_APP_API_URL}/${doc.isCustomerUpload ? doc.customerFilePath : doc.filePath}`, '_blank')}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          View
                        </Button>
                        {doc?.customerFilePath && !doc.isCustomerUpload && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(`${process.env.REACT_APP_API_URL}${doc.customerFilePath}`, '_blank')}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            View Customer Docs
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Card sx={{
                borderRadius: 4,
                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.15)',
                textAlign: 'center',
                py: 10,
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '2px dashed rgba(59, 130, 246, 0.2)',
              }}>
                <CardContent>
                  <Box sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 4,
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                  }}>
                    <Description sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                    No Documents Available
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                    This banker has not uploaded any documents yet.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                    Document information will appear here once the banker uploads files for their deals.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
        </div>
        </div>
  );
}
